/**
 * Importing a bank export or a card statement: CSV, XLSX, JSON or PDF.
 *
 * Nothing is written until the user has seen the plan: how many rows are new, already known,
 * merged with the same charge from another format, flagged for review, or left out (paying off
 * the card). The identical file is refused up front, and a statement for another card is called
 * out before it lands on the wrong account.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCommitImport } from "@/hooks/use-banking";
import type { BankAccount } from "@/lib/api/banking";
import {
  findImport,
  hashFile,
  loadAccountRows,
  readStatementFile,
  type ReadStatement,
  type StatementImport,
} from "@/lib/api/statements";
import { useAuth } from "@/lib/session/auth-context";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { planImport, type PlanKind } from "@/lib/import/reconcile";
import { cardLast4, detectFormat, type StatementFormat } from "@/lib/import/statement";
import {
  guessMapping,
  rowsToStatementRows,
  type ColumnKey,
  type ColumnMapping,
} from "@/lib/import/transactions";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

const FIELDS: ColumnKey[] = [
  "bookingDate",
  "valueDate",
  "amount",
  "debit",
  "credit",
  "counterparty",
  "description",
  "reference",
  "currency",
];

const NONE = "none";
const KINDS: PlanKind[] = ["create", "known", "merge", "review", "excluded"];

const OUTCOME_CLASS: Record<PlanKind, string> = {
  create: "text-foreground",
  known: "text-muted-foreground",
  merge: "text-success",
  review: "text-warning",
  excluded: "text-muted-foreground",
};

type Loaded = {
  file: File;
  hash: string;
  format: StatementFormat;
  read: ReadStatement;
};

export function StatementImportDialog({
  open,
  onOpenChange,
  accounts,
  defaultAccountId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts: BankAccount[];
  defaultAccountId?: string;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspaceId } = useWorkspace();
  const commit = useCommitImport(workspaceId, user?.id ?? "");

  const [accountId, setAccountId] = useState(defaultAccountId ?? accounts[0]?.id ?? "");
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [invertAmounts, setInvertAmounts] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [duplicateFile, setDuplicateFile] = useState<StatementImport | null>(null);

  const account = accounts.find((entry) => entry.id === accountId);

  // Always fresh: the plan is only as good as its view of what the account already holds.
  const { data: accountRows, isFetching: loadingRows } = useQuery({
    queryKey: ["banking", "account-rows", accountId, loaded?.hash],
    queryFn: () => loadAccountRows(accountId),
    enabled: Boolean(accountId && loaded),
    staleTime: 0,
    gcTime: 0,
  });

  function reset() {
    setLoaded(null);
    setMapping({});
    setInvertAmounts(false);
    setError("");
    setDuplicateFile(null);
  }

  async function readFile(file: File | undefined) {
    reset();
    if (!file || !accountId) return;
    setReading(true);
    try {
      const hash = await hashFile(file);
      const previous = await findImport(accountId, hash);
      if (previous) {
        setDuplicateFile(previous);
        return;
      }
      const format = detectFormat(file.name, new Uint8Array(await file.slice(0, 64).arrayBuffer()));
      const read = await readStatementFile(file, format);
      if (read.kind === "table") {
        if (read.table.length < 2) {
          setError(t("import.empty"));
          return;
        }
        const preset = account?.importPreset;
        setMapping(
          preset && Object.keys(preset).length > 0 ? preset : guessMapping(read.table[0]!),
        );
      } else if (read.rows.length === 0) {
        setError(t("import.empty"));
        return;
      }
      setLoaded({ file, hash, format, read });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setError(message === "unsupported_json" ? t("import.unsupportedJson") : message);
    } finally {
      setReading(false);
    }
  }

  const parsed = useMemo(() => {
    if (!loaded) return null;
    if (loaded.read.kind === "rows") return { rows: loaded.read.rows, errors: [] };
    return rowsToStatementRows(loaded.read.table.slice(1), mapping, { invertAmounts });
  }, [loaded, mapping, invertAmounts]);

  const plan = useMemo(
    () =>
      parsed && accountRows
        ? planImport(parsed.rows, accountRows.existing, { excludeSettlements: true })
        : null,
    [parsed, accountRows],
  );

  // A statement or export for another card is almost always a mistake.
  const fileCard = loaded
    ? cardLast4(
        loaded.read.kind === "rows"
          ? (loaded.read.cardNumber ?? loaded.file.name)
          : loaded.file.name,
      )
    : undefined;
  const cardMismatch = Boolean(fileCard && account?.last4 && fileCard !== account.last4);

  async function submit() {
    if (!loaded || !plan || !accountRows || !accountId) return;
    try {
      const record = await commit.mutateAsync({
        accountId,
        plan,
        records: accountRows.records,
        file: {
          name: loaded.file.name,
          hash: loaded.hash,
          format: loaded.format,
          family: loaded.read.family,
          statementNumber: loaded.read.kind === "rows" ? loaded.read.statementNumber : undefined,
        },
        preset: loaded.read.kind === "table" ? mapping : undefined,
      });
      toast.success(
        t("import.done", {
          created: record.createdCount + record.reviewCount,
          merged: record.mergedCount,
          known: record.knownCount,
        }),
      );
      onOpenChange(false);
      reset();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : t("import.failed"));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("import.title")}</DialogTitle>
          <DialogDescription>{t("import.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="import-account">{t("import.account")}</Label>
            <Select
              value={accountId}
              onValueChange={(value) => {
                setAccountId(value);
                reset();
              }}
            >
              <SelectTrigger id="import-account" data-testid="import-account">
                <SelectValue placeholder={t("import.account")} />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((entry) => (
                  <SelectItem key={entry.id} value={entry.id}>
                    {entry.label}
                    {entry.last4 ? ` ···· ${entry.last4}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="import-file">{t("import.file")}</Label>
            <input
              id="import-file"
              type="file"
              accept=".csv,.txt,.xlsx,.json,.pdf,text/csv,application/json,application/pdf"
              data-testid="import-file"
              disabled={!accountId || reading}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-secondary file:px-3 file:py-1.5 file:text-sm"
              onChange={(event) => {
                void readFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <p className="text-xs text-muted-foreground">{t("import.formats")}</p>
          </div>
        </div>

        {reading && <p className="text-sm text-muted-foreground">{t("import.reading")}</p>}

        {error && (
          <p role="alert" className="text-sm text-destructive" data-testid="import-error">
            {error}
          </p>
        )}

        {duplicateFile && (
          <Alert data-testid="import-already">
            <AlertTitle>{t("import.alreadyTitle")}</AlertTitle>
            <AlertDescription>
              {t("import.alreadyText", { date: formatDate(duplicateFile.created) })}
            </AlertDescription>
          </Alert>
        )}

        {cardMismatch && (
          <Alert variant="destructive" data-testid="import-card-warning">
            <AlertTitle>{t("import.cardMismatchTitle")}</AlertTitle>
            <AlertDescription>
              {t("import.cardMismatchText", { file: fileCard, account: account?.last4 })}
            </AlertDescription>
          </Alert>
        )}

        {loaded?.read.kind === "table" && (
          <>
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("import.columns")}</p>
              <p className="text-xs text-muted-foreground">
                {t("import.columnsHint", { file: loaded.file.name })}
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {FIELDS.map((field) => (
                  <div key={field} className="space-y-1">
                    <Label className="text-xs text-muted-foreground" htmlFor={`map-${field}`}>
                      {t(`import.fields.${field}`)}
                    </Label>
                    <Select
                      value={mapping[field] === undefined ? NONE : String(mapping[field])}
                      onValueChange={(value) =>
                        setMapping((current) => ({
                          ...current,
                          [field]: value === NONE ? undefined : Number(value),
                        }))
                      }
                    >
                      <SelectTrigger id={`map-${field}`} data-testid={`map-${field}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>{t("import.notPresent")}</SelectItem>
                        {(loaded.read.kind === "table" ? loaded.read.table[0]! : []).map(
                          (column, index) => (
                            <SelectItem key={`${column}-${index}`} value={String(index)}>
                              {column || `#${index + 1}`}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-md border p-3">
              <Switch
                id="invert"
                checked={invertAmounts}
                onCheckedChange={setInvertAmounts}
                data-testid="invert-amounts"
              />
              <div>
                <Label htmlFor="invert">{t("import.invert")}</Label>
                <p className="text-xs text-muted-foreground">{t("import.invertHint")}</p>
              </div>
            </div>
          </>
        )}

        {loaded && loadingRows && !plan && (
          <p className="text-sm text-muted-foreground">{t("import.comparing")}</p>
        )}

        {plan && parsed && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-2" data-testid="import-summary">
              {KINDS.map((kind) => (
                <div key={kind} className="min-w-20">
                  <p className="text-xs text-muted-foreground">{t(`import.outcome.${kind}`)}</p>
                  <p
                    className={cn(
                      "text-xl font-semibold num",
                      plan.counts[kind] > 0 && OUTCOME_CLASS[kind],
                    )}
                    data-testid={`import-count-${kind}`}
                  >
                    {plan.counts[kind]}
                  </p>
                </div>
              ))}
              {parsed.errors.length > 0 && (
                <div className="min-w-20">
                  <p className="text-xs text-muted-foreground">{t("import.unreadable")}</p>
                  <p
                    className="text-xl font-semibold text-destructive num"
                    data-testid="import-count-unreadable"
                  >
                    {parsed.errors.length}
                  </p>
                </div>
              )}
            </div>
            {plan.counts.review > 0 && (
              <p className="text-xs text-muted-foreground">{t("import.reviewHint")}</p>
            )}

            <div className="max-h-72 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("import.fields.bookingDate")}</TableHead>
                    <TableHead>{t("import.fields.counterparty")}</TableHead>
                    <TableHead className="text-right">{t("import.fields.amount")}</TableHead>
                    <TableHead>{t("import.result")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.items.map((item, index) => (
                    <TableRow key={index} data-testid="import-preview-row" data-outcome={item.kind}>
                      <TableCell className="text-muted-foreground num">
                        {formatDate(item.row.date)}
                      </TableCell>
                      <TableCell className="max-w-64 truncate">{item.row.merchant}</TableCell>
                      <TableCell className="text-right num">
                        {formatMoney(item.row.amountCents / 100, item.row.currency)}
                      </TableCell>
                      <TableCell className={cn("text-xs font-medium", OUTCOME_CLASS[item.kind])}>
                        {t(`import.outcome.${item.kind}`)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              !plan ||
              commit.isPending ||
              plan.counts.create + plan.counts.merge + plan.counts.review === 0
            }
            data-testid="confirm-import"
          >
            {t("import.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
